import { useMemo, useEffect, useContext, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { getFormattedDuration } from 'utils/date';

import { LocaleContext } from 'context/LocaleContext';

import FieldDefault from 'components/field-default';
import Select from 'components/select';

const ONE_HOUR_IN_SECONDS = 60 * 60;

const convertTo12h = time => {
    let [hours, minutes] = time.split(':');

    hours = Number(hours);

    if (hours > 12) {
        hours = hours - 12;
    }

    if (hours === 0) {
        hours = 12;
    }

    return `${hours.toString().padStart(2, '0')}:${minutes.padStart(2, '0')}`;
}

const TimeSelector = ({ value, onChange }) => {
    const { t } = useTranslation('recurring');
    const locale = useContext(LocaleContext);
    const fieldCtrl = useRef(null);

    const midnightOptions = useMemo(() => [
        {
            id: 'am',
            text: t('date:AM')
        },
        {
            id: 'pm',
            text: t('date:PM')
        }
    ], [t]);

    const timeValue = useMemo(() => {
        const result = {};

        if (locale?.timeFormat === '12h') {
            const amTime = value.time - 12 * ONE_HOUR_IN_SECONDS;

            if (value.time < ONE_HOUR_IN_SECONDS) {
                return {
                    time: getFormattedDuration(1000 * (12 * ONE_HOUR_IN_SECONDS + value.time), { seconds: false }),
                    midnight: 'am'
                };
            } else if (amTime >= 0 && amTime < ONE_HOUR_IN_SECONDS) {
                return {
                    time: getFormattedDuration(1000 * value.time, { seconds: false }),
                    midnight: 'pm'
                };
            } else if (amTime > 0) {
                return {
                    time: getFormattedDuration(1000 * amTime, { seconds: false }),
                    midnight: 'pm'
                };
            } else {
                return {
                    time: getFormattedDuration(1000 * value.time, { seconds: false }),
                    midnight: 'am'
                };
            }

        } else {
            return {
                time: getFormattedDuration(1000 * value.time, { seconds: false }),
            }
        }
    }, [value?.time, locale?.timeFormat]);

    const updateTime = useCallback((nextStartAt, nextStartAtMidnight) => {
        if (nextStartAt.match(/^\d{4}$/)) {
            const [h1, h2, m1, m2] = nextStartAt.split('');
            nextStartAt = `${h1}${h2}:${m1}${m2}`;
        }

        if (!nextStartAt.match(/^\d{0,2}:\d{0,2}$/)) {
            nextStartAt = '09:00';
        }

        let [hours, minutes] = nextStartAt.split(':');
        hours = Number(hours);

        if (locale.timeFormat === '12h') {
            if (hours === 0) {
                hours = 12;
            }

            if (nextStartAtMidnight === 'pm') {
                if (hours !== 12) {
                    hours = (hours + 12).toString();
                }
            } else {
                if (hours >= 12) {
                    if (hours > 12) {
                        // nextStartAtMidnight = 'pm';
                    }

                    hours = (hours - 12).toString();
                }
            }
        }

        if (hours - 0 > 23) {
            hours = 23;
        }

        if (Number(minutes) > 60) {
            minutes = '60';
        }

        nextStartAt = `${hours.toString().padStart(2, '0')}:${minutes.padStart(2, '0')}`;

        if (fieldCtrl.current) {
            if (locale.timeFormat === '12h') {
                if (convertTo12h(nextStartAt) !== fieldCtrl.current.getValue()) {
                    fieldCtrl.current.setValue(convertTo12h(nextStartAt));
                }
            } else {
                if (nextStartAt !== fieldCtrl.current.getValue()) {
                    fieldCtrl.current.setValue(nextStartAt);
                }
            }
        }

        onChange(hours * ONE_HOUR_IN_SECONDS + minutes * 60);
    }, [locale?.timeFormat, fieldCtrl, onChange])

    const handleBlur = useCallback(nextStartAt => {
        updateTime(nextStartAt, timeValue.midnight);
    }, [timeValue, updateTime]);


    const handleMidnightChange = useCallback(({ id }) => {
        updateTime(timeValue.time, id);
    }, [timeValue, updateTime]);

    useEffect(() => {
        if (!value.time) {
            onChange(ONE_HOUR_IN_SECONDS * 9);
        }
    }, [value, onChange]);

    return (
        <div className="resource-repeat-row">
            <div className="row-label">
                { t('At (time)') }
            </div>

            <div className="row-value">
                <div className="row-input">
                    <FieldDefault
                        small={true}
                        value={timeValue.time}
                        onBlur={handleBlur}
                        provideController={fieldCtrl}
                    />
                </div>

                {
                    locale.timeFormat === '12h' ? (
                        <div className="row-input">
                            <Select
                                small={true}
                                selected={timeValue.midnight}
                                options={midnightOptions}
                                onChange={handleMidnightChange}
                            />
                        </div>
                    ) : null
                }
            </div>
        </div>
    )
}

export default TimeSelector;
